import { useState, useEffect } from "react";
import { ModalShell } from "@/components/ui/modal-shell";
import { Button } from "@/components/ui/button";
import { SortableList } from "@/components/SortableList";
import { Loader2, GripVertical } from "lucide-react";

interface ReorderSection {
  label: string;
  items: { id: string; name: string }[];
}

interface ReorderTab {
  key: string;
  label: string;
  items?: { id: string; name: string }[];
  sections?: ReorderSection[];
  onSave: (items: { id: string; sortOrder: number }[]) => Promise<void>;
}

interface ReorderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tabs: ReorderTab[];
}

export function ReorderDialog({ open, onOpenChange, tabs }: ReorderDialogProps) {
  const [activeTab, setActiveTab] = useState(tabs[0]?.key || "");
  const [localItems, setLocalItems] = useState<Record<string, { id: string; name: string }[]>>({});
  const [localSections, setLocalSections] = useState<Record<string, ReorderSection[]>>({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (open) {
      const itemMap: Record<string, { id: string; name: string }[]> = {};
      const sectionMap: Record<string, ReorderSection[]> = {};
      for (const tab of tabs) {
        if (tab.sections) {
          sectionMap[tab.key] = tab.sections.map(s => ({ label: s.label, items: [...s.items] }));
        } else if (tab.items) {
          itemMap[tab.key] = [...tab.items];
        }
      }
      setLocalItems(itemMap);
      setLocalSections(sectionMap);
      setDirty({});
      setActiveTab(tabs[0]?.key || "");
    }
  }, [open, tabs]);

  const handleReorder = (tabKey: string, newItems: { id: string; name: string }[]) => {
    setLocalItems((prev) => ({ ...prev, [tabKey]: newItems }));
    setDirty((prev) => ({ ...prev, [tabKey]: true }));
  };

  const handleSectionReorder = (tabKey: string, sectionIndex: number, newItems: { id: string; name: string }[]) => {
    setLocalSections((prev) => {
      const sections = [...(prev[tabKey] || [])];
      sections[sectionIndex] = { ...sections[sectionIndex], items: newItems };
      return { ...prev, [tabKey]: sections };
    });
    setDirty((prev) => ({ ...prev, [tabKey]: true }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const tab of tabs) {
        if (dirty[tab.key]) {
          if (localSections[tab.key]) {
            const allItems: { id: string; sortOrder: number }[] = [];
            for (const section of localSections[tab.key]) {
              section.items.forEach((item, index) => {
                allItems.push({ id: item.id, sortOrder: index });
              });
            }
            await tab.onSave(allItems);
          } else if (localItems[tab.key]) {
            const items = localItems[tab.key].map((item, index) => ({
              id: item.id,
              sortOrder: index,
            }));
            await tab.onSave(items);
          }
        }
      }
      onOpenChange(false);
    } catch (err) {
      console.error("Reorder save error:", err);
    } finally {
      setSaving(false);
    }
  };

  const hasDirty = Object.values(dirty).some(Boolean);

  return (
    <ModalShell
      open={open}
      onOpenChange={onOpenChange}
      title="Ordenar Itens"
      subtitle={
        // Texto corrido com o ícone inline: em flex cada pedaço de texto
        // virava uma caixa e quebrava separado ("Arraste pelos / pontinhos").
        <span>
          Arraste pelos pontinhos <GripVertical className="inline w-3.5 h-3.5 align-[-2px]" aria-hidden /> para mudar a ordem.
        </span>
      }
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={!hasDirty || saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Salvar Ordem
          </Button>
        </>
      }
    >
        {tabs.length > 1 && (
          <div className="flex gap-1 border-b border-border/50 pb-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {tab.label}
                {dirty[tab.key] && <span className="ml-1 text-xs">•</span>}
              </button>
            ))}
          </div>
        )}

        <div className="max-h-[400px] overflow-y-auto -mx-1 px-1">
          {tabs.map((tab) => {
            if (tab.key !== activeTab) return null;

            const sections = localSections[tab.key];
            if (sections) {
              const allEmpty = sections.every(s => s.items.length === 0);
              if (allEmpty) {
                return (
                  <p key={tab.key} className="text-sm text-muted-foreground text-center py-8">
                    Nenhum item para ordenar
                  </p>
                );
              }
              return (
                <div key={tab.key} className="space-y-4">
                  {sections.map((section, si) => {
                    if (section.items.length === 0) return null;
                    return (
                      <div key={section.label}>
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
                          {section.label}
                        </p>
                        <SortableList
                          items={section.items}
                          onReorder={(newItems) => handleSectionReorder(tab.key, si, newItems)}
                          className="space-y-1"
                          renderItem={(item) => (
                            <div className="flex items-center py-2.5 px-3 bg-card border border-border/50 rounded-lg">
                              <span className="text-sm font-medium truncate">{item.name}</span>
                            </div>
                          )}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            }

            const items = localItems[tab.key] || [];
            if (items.length === 0) {
              return (
                <p key={tab.key} className="text-sm text-muted-foreground text-center py-8">
                  Nenhum item para ordenar
                </p>
              );
            }
            return (
              <SortableList
                key={tab.key}
                items={items}
                onReorder={(newItems) => handleReorder(tab.key, newItems)}
                className="space-y-1"
                renderItem={(item) => (
                  <div className="flex items-center py-2.5 px-3 bg-card border border-border/50 rounded-lg">
                    <span className="text-sm font-medium truncate">{item.name}</span>
                  </div>
                )}
              />
            );
          })}
        </div>
    </ModalShell>
  );
}
